// src/components/lesson/diagrams/RightTriangleAltitude.tsx
//
// A right triangle with the altitude drawn from the RIGHT-ANGLE vertex to the
// hypotenuse — the canonical "geometric mean / altitude-on-hypotenuse" figure
// (△ACD ~ △CBD ~ △ABC). The plain `right-triangle` type can't show this: it
// has no internal cevian and no vertex labels, so a Sage explanation that talks
// about points A, B, C, D and segments AD, DB, CD had nowhere correct to draw.
//
// Convention (matches how these are drawn in textbooks):
//
//            C            ← right angle at the apex (angle ACB = 90°)
//           /|\
//     (AC) / |h\ (BC)
//         /  |  \
//        A---D---B        ← hypotenuse AB; D is the foot of the altitude
//          AD   DB
//
// The apex C sits directly above the foot D at height √(AD·DB) — the geometric
// mean — so angle ACB is exactly 90° and the figure is geometrically valid for
// any AD, DB the caller passes. Labels are arbitrary strings (the drawing stays
// proportional), same contract as the other geometry renderers.
//
// Params:
//   {
//     "ad": 8, "db": 18,               // hypotenuse segments (drive the shape)
//     "adLabel": "8", "dbLabel": "18", // segment length labels (optional)
//     "cdLabel": "12",                 // altitude length label (optional)
//     "acLabel": "…", "bcLabel": "…",  // leg labels (optional)
//     "aLabel": "A", "bLabel": "B",    // vertex labels (default A/B/C/D)
//     "cLabel": "C", "dLabel": "D",
//     "label": "…"                      // accessible name
//   }

type Params = {
  ad?: number
  db?: number
  adLabel?: string
  dbLabel?: string
  cdLabel?: string
  acLabel?: string
  bcLabel?: string
  aLabel?: string
  bLabel?: string
  cLabel?: string
  dLabel?: string
  label?: string
}

const W = 360
const H = 260
const PAD = 44

const SLATE = 'rgb(148 163 184)' // outline + secondary length labels (matches sibling diagrams)
const ALT = 'rgb(251 191 36)' // amber — the altitude CD, so it reads as the constructed segment
const VERTEX = 'rgb(34 211 238)' // cyan — the named points A/B/C/D, the primary labels the reader hunts for

export default function RightTriangleAltitude({ params }: { params: Record<string, unknown> }) {
  const p = params as Params
  const ad = typeof p.ad === 'number' && p.ad > 0 ? p.ad : 8
  const db = typeof p.db === 'number' && p.db > 0 ? p.db : 18
  const total = ad + db
  const h = Math.sqrt(ad * db) // geometric mean → guarantees the right angle at C

  // Fit the (total × h) figure into the canvas, preserving proportions.
  const availW = W - 2 * PAD
  const availH = H - 2 * PAD
  const scale = Math.min(availW / total, availH / h)

  const baseY = H - PAD
  const leftX = (W - total * scale) / 2
  const A = { x: leftX, y: baseY }
  const B = { x: leftX + total * scale, y: baseY }
  const D = { x: leftX + ad * scale, y: baseY }
  const C = { x: D.x, y: baseY - h * scale }

  // Right-angle marker at C, oriented along the two legs CA and CB.
  const s = Math.min(15, h * scale * 0.28)
  const uCA = unit(A.x - C.x, A.y - C.y)
  const uCB = unit(B.x - C.x, B.y - C.y)
  const cMarker = [
    { x: C.x + uCA.x * s, y: C.y + uCA.y * s },
    { x: C.x + (uCA.x + uCB.x) * s, y: C.y + (uCA.y + uCB.y) * s },
    { x: C.x + uCB.x * s, y: C.y + uCB.y * s },
  ]
  const ds = Math.min(11, Math.min(ad, db) * scale * 0.35)

  const lab = (v: string | undefined, fallback: string) =>
    typeof v === 'string' && v.trim() ? v : fallback

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={p.label ?? 'Right triangle with the altitude to the hypotenuse'}
      className="my-3 w-full max-w-md rounded-md bg-card/60 ring-1 ring-slate-800"
    >
      {/* Triangle body */}
      <polygon
        points={`${A.x},${A.y} ${B.x},${B.y} ${C.x},${C.y}`}
        fill="rgb(34 211 238 / 0.10)"
        stroke={SLATE}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />

      {/* Altitude CD (the constructed segment) */}
      <line x1={C.x} y1={C.y} x2={D.x} y2={D.y} stroke={ALT} strokeWidth="2" />

      {/* Right-angle marker at the apex C (angle ACB) */}
      <polyline
        points={cMarker.map((m) => `${m.x},${m.y}`).join(' ')}
        fill="none"
        stroke={SLATE}
        strokeWidth="1.25"
      />
      {/* Right-angle marker at the foot D (altitude ⊥ hypotenuse), on the DB side */}
      <polyline
        points={`${D.x + ds},${D.y} ${D.x + ds},${D.y - ds} ${D.x},${D.y - ds}`}
        fill="none"
        stroke={ALT}
        strokeWidth="1.25"
      />

      {/* Vertex labels */}
      <text x={A.x - 6} y={A.y + 16} fontSize="13" fontWeight="700" textAnchor="end" fill={VERTEX}>
        {lab(p.aLabel, 'A')}
      </text>
      <text
        x={B.x + 6}
        y={B.y + 16}
        fontSize="13"
        fontWeight="700"
        textAnchor="start"
        fill={VERTEX}
      >
        {lab(p.bLabel, 'B')}
      </text>
      <text x={C.x} y={C.y - 8} fontSize="13" fontWeight="700" textAnchor="middle" fill={VERTEX}>
        {lab(p.cLabel, 'C')}
      </text>
      <text x={D.x} y={D.y + 16} fontSize="13" fontWeight="700" textAnchor="middle" fill={VERTEX}>
        {lab(p.dLabel, 'D')}
      </text>

      {/* Hypotenuse segment labels (row below the vertex letters) */}
      {p.adLabel && (
        <text
          x={(A.x + D.x) / 2}
          y={baseY + 32}
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
          fill={SLATE}
        >
          {p.adLabel}
        </text>
      )}
      {p.dbLabel && (
        <text
          x={(D.x + B.x) / 2}
          y={baseY + 32}
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
          fill={SLATE}
        >
          {p.dbLabel}
        </text>
      )}

      {/* Altitude label — to the right of CD, inside the (wider) DB sub-triangle */}
      {p.cdLabel && (
        <text
          x={C.x + 7}
          y={(C.y + D.y) / 2}
          fontSize="12"
          fontWeight="600"
          textAnchor="start"
          dominantBaseline="middle"
          fill={ALT}
        >
          {p.cdLabel}
        </text>
      )}

      {/* Optional leg labels, placed just outside each leg's midpoint */}
      {p.acLabel && (
        <text
          x={(A.x + C.x) / 2 - 8}
          y={(A.y + C.y) / 2 - 4}
          fontSize="12"
          fontWeight="600"
          textAnchor="end"
          fill={SLATE}
        >
          {p.acLabel}
        </text>
      )}
      {p.bcLabel && (
        <text
          x={(B.x + C.x) / 2 + 8}
          y={(B.y + C.y) / 2 - 4}
          fontSize="12"
          fontWeight="600"
          textAnchor="start"
          fill={SLATE}
        >
          {p.bcLabel}
        </text>
      )}
    </svg>
  )
}

function unit(dx: number, dy: number): { x: number; y: number } {
  const m = Math.hypot(dx, dy) || 1
  return { x: dx / m, y: dy / m }
}
